import Breadcrumb from "components/Common/Breadcrumb";
import TableContainer from "components/Common/DataTableContainer";
import DashboardLoader from "components/Loader/DashboardLoader";
import DeleteModal from "pages/Calendar/DeleteModal";
import React, { useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { selectUser } from "store/features/auth/authSlice";
import {
  useGetAllWorkLogsQuery,
  useDeleteWorkLogMutation,
  useCreateWorkLogMutation,
  useUpdateWorkLogMutation,
} from "store/features/client/clientManagementApi";
import { useGetAllProjectsQuery } from "store/features/client/clientManagementApi";
import WorkLogModal from "./WorkLogModal";

const AllWorkLogs = () => {
  const user = useSelector(selectUser);

  const { data: workLogs, isLoading, refetch } = useGetAllWorkLogsQuery();
  const { data: projects = [] } = useGetAllProjectsQuery();
  const [deleteWorkLog, { isLoading: deletingWorkLog }] = useDeleteWorkLogMutation();
  const [createWorkLog, { isLoading: creatingWorkLog }] = useCreateWorkLogMutation();
  const [updateWorkLog, { isLoading: updatingWorkLog }] = useUpdateWorkLogMutation();

  const [deleteModal, setDeleteModal] = useState(false);
  const [workLogModal, setWorkLogModal] = useState(false);
  const [selectedWorkLogId, setSelectedWorkLogId] = useState(null);
  const [editingWorkLog, setEditingWorkLog] = useState(null);
  const [dateFilter, setDateFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [searchTerm, setSearchTerm] = useState("");

  // Get today's date in YYYY-MM-DD format
  const getTodayDate = () => {
    return new Date().toISOString().split('T')[0];
  };

  // Get project name by ID
  const getProjectName = projectId => {
    if (!projectId) return "No Project";
    console.log("projectId", projectId);

    const project = projects.find(project => project.id == projectId);
    return project ? project.name : "Unknown Project";
  };

  // Get status badge
  const getStatusBadge = status => {
    const statusConfig = {
      'Completed': 'success',
      'In Progress': 'primary',
      'Pending': 'warning',
      'Blocked': 'danger'
    };
    const color = statusConfig[status] || 'secondary';
    return `<span class="badge bg-${color}">${status}</span>`;
  };

  // Filter work logs based on selected filters and user role
  const filteredWorkLogs = useMemo(() => {
    if (!workLogs) return [];

    let filtered = workLogs;

    // Apply date filter (default to today if no filter)
    if (!dateFilter) {
      filtered = filtered.filter(log => log.date == getTodayDate());
    } else {
      filtered = filtered.filter(log => log.date == dateFilter);
    }

    // Apply status filter
    if (statusFilter) {
      filtered = filtered.filter(log => log.work_status == statusFilter);
    }

    // Apply search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(log =>
        log.work_summary?.toLowerCase().includes(term) ||
        log.title?.toLowerCase().includes(term) ||
        log.employer_name?.toLowerCase().includes(term)
      );
    }

    const cleanRole = String(user?.role || "").toLowerCase().trim().replace(/[-\s]/g, "_");
    const isAdmin = cleanRole === "super_admin" || cleanRole === "admin";

    // Apply role-based filtering
    if (!isAdmin) {
      filtered = filtered.filter(log => String(log.employer_id) === String(user?.id));
    }

    return filtered;
  }, [workLogs, dateFilter, statusFilter, searchTerm, user]);

  const cleanRole = String(user?.role || "").toLowerCase().trim().replace(/[-\s]/g, "_");
  const isAdmin = cleanRole === "super_admin" || cleanRole === "admin";

  const isUserAssigned = (assignedList, userId) => {
    if (!assignedList) return false;
    const list = Array.isArray(assignedList) ? assignedList : [assignedList];
    return list.some(idOrUser => {
      if (typeof idOrUser === "object" && idOrUser !== null) {
        return String(idOrUser.id) === String(userId);
      }
      return String(idOrUser) === String(userId);
    });
  };

  const selectableProjects = useMemo(() => {
    if (isAdmin) return projects;
    return projects.filter(p =>
      isUserAssigned(p.assigned_frontend_dev, user?.id) ||
      isUserAssigned(p.assigned_backend_dev, user?.id) ||
      isUserAssigned(p.assigned_designer, user?.id) ||
      isUserAssigned(p.assigned_team, user?.id) ||
      isUserAssigned(p.assigned_project_manager, user?.id)
    );
  }, [projects, isAdmin, user]);

  const showDeleteModal = id => {
    setSelectedWorkLogId(id);
    setDeleteModal(true);
  };

  const showAddModal = () => {
    setEditingWorkLog(null);
    setWorkLogModal(true);
  };

  const showEditModal = workLog => {
    setEditingWorkLog(workLog);
    setWorkLogModal(true);
  };

  const handleDeleteWorkLog = () => {
    deleteWorkLog(selectedWorkLogId);
    setDeleteModal(false);
  };

  const handleWorkLogSubmit = async (formData) => {
    try {
      if (editingWorkLog) {
        // Update existing work log
        await updateWorkLog({
          id: editingWorkLog.id,
          ...formData
        }).unwrap();
      } else {
        // Create new work log
        const workLogData = {
          ...formData,
          employer_id: user.id,
          employer_name: user.nickname || user.name,
          employer_role: user.role,
          hours_spent: parseFloat(formData.hours_spent) || 0
        };
        await createWorkLog(workLogData).unwrap();
      }

      setWorkLogModal(false);
      setEditingWorkLog(null);
      refetch();
    } catch (error) {
      console.error('Work log operation error:', error);
    }
  };

  const handleModalClose = () => {
    setWorkLogModal(false);
    setEditingWorkLog(null);
  };

  const columns = useMemo(() => [
    {
      header: "Employee",
      accessorKey: "employer_name",
      enableColumnFilter: false,
      enableSorting: true,
      cell: ({ row }) => (
        <div>
          <div className="">{row.original.employer_name}</div>
          <small className="text-muted">{row.original.employer_role}</small>
        </div>
      ),
    },
        {
      header: "Date",
      accessorKey: "date",
      enableColumnFilter: false,
      enableSorting: true,
      cell: ({ row }) => (
        <div className="">
          {new Date(row.original.date).toLocaleDateString()}
        </div>
      ),
    },
    {
      header: "Hours",
      accessorKey: "hours_spent",
      enableColumnFilter: false,
      enableSorting: true,
      cell: ({ row }) => (
        <div className="">
          <span className="badge bg-info fs-6">{row.original.hours_spent}h</span>
        </div>
      ),
    },
    {
      header: "Project",
      accessorKey: "project_id",
      enableColumnFilter: false,
      enableSorting: true,
      cell: ({ row }) => (
        <div className="">
          {getProjectName(row.original.project_id)}
        </div>
      ),
    },
    // {
    //   header: "Title",
    //   accessorKey: "title",
    //   enableColumnFilter: false,
    //   enableSorting: true,
    //   cell: ({ row }) => (
    //     <div className="fw-semibold">
    //       {row.original.title || "No Title"}
    //     </div>
    //   ),
    // },
    {
      header: "Work Summary",
      accessorKey: "work_summary",
      enableColumnFilter: false,
      enableSorting: true,
      cell: ({ row }) => (
        <div className="" style={{
          whiteSpace: 'normal',
          wordWrap: 'break-word',
          wordBreak: 'break-word'
        }}>{row.original.work_summary}</div>


      ),
    },

    // {
    //   header: "Status",
    //   accessorKey: "work_status",
    //   enableColumnFilter: false,
    //   enableSorting: true,
    //   cell: ({ row }) => (
    //     <div
    //       dangerouslySetInnerHTML={{
    //         __html: getStatusBadge(row.original.work_status),
    //       }}
    //     />
    //   ),
    // },
    {
      header: "Action",
      accessorKey: "action",
      enableColumnFilter: false,
      enableSorting: true,
      cell: ({ row }) => {
        const canEdit = user.role == "super_admin" || user.role == "admin" || row.original.employer_id == user.id;

        return (
          <div className="d-flex align-items-center gap-1">
            <button
              className="btn btn-warning btn-sm"
              onClick={() => showEditModal(row.original)}
              disabled={!canEdit}
              title={canEdit ? "Edit Work Log" : "You can only edit your own work logs"}
            >
              <i className="fas fa-edit"></i>
            </button>

            {(user?.role == "super_admin" || user?.role == "admin") && (
              <button
                className="btn btn-danger btn-sm"
                onClick={() => showDeleteModal(row.original.id)}
              >
                <i className="fas fa-trash"></i>
                {/* {deletingWorkLog ? "Deleting..." : "Delete"} */}
              </button>
            )}
          </div>
        );
      },
    },
  ], [projects, deletingWorkLog, user]);

  if (isLoading) {
    return <DashboardLoader />;
  }

  return (
    <div className="page-content">
      <div className="container-fluid">
        <Breadcrumb title="Work Log" breadcrumbItem="Work Logs" />

        {/* Quick Stats */}
        {/* <div className="row mb-4">
          <div className="col-md-3">
            <div className="card bg-primary text-white">
              <div className="card-body">
                <div className="d-flex align-items-center">
                  <div className="flex-grow-1">
                    <h4 className="mb-0">{filteredWorkLogs.length}</h4>
                    <span>Total Entries</span>
                  </div>
                  <div className="flex-shrink-0">
                    <i className="fas fa-clipboard-list fa-2x"></i>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="col-md-3">
            <div className="card bg-success text-white">
              <div className="card-body">
                <div className="d-flex align-items-center">
                  <div className="flex-grow-1">
                    <h4 className="mb-0">
                      {filteredWorkLogs.reduce((sum, log) => sum + parseFloat(log.hours_spent || 0), 0).toFixed(1)}
                    </h4>
                    <span>Total Hours</span>
                  </div>
                  <div className="flex-shrink-0">
                    <i className="fas fa-clock fa-2x"></i>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="col-md-3">
            <div className="card bg-info text-white">
              <div className="card-body">
                <div className="d-flex align-items-center">
                  <div className="flex-grow-1">
                    <h4 className="mb-0">
                      {filteredWorkLogs.filter(log => log.work_status == 'Completed').length}
                    </h4>
                    <span>Completed</span>
                  </div>
                  <div className="flex-shrink-0">
                    <i className="fas fa-check-circle fa-2x"></i>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="col-md-3">
            <div className="card bg-warning text-white">
              <div className="card-body">
                <div className="d-flex align-items-center">
                  <div className="flex-grow-1">
                    <h4 className="mb-0">
                      {filteredWorkLogs.filter(log => log.work_status == 'In Progress').length}
                    </h4>
                    <span>In Progress</span>
                  </div>
                  <div className="flex-shrink-0">
                    <i className="fas fa-spinner fa-2x"></i>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div> */}

        {/* Filters Section */}
        <div className="d-flex justify-content-between mb-3">
          <div className="d-flex">
            <div className="me-2">
              <label className="form-label">Date Filter</label>
              <input
                type="date"
                className="form-control"
                value={dateFilter}
                onChange={e => setDateFilter(e.target.value)}
              />
              {/* {dateFilter && (
                <small
                  className="text-primary cursor-pointer"
                  onClick={() => setDateFilter("")}
                >
                  Show Today's Logs
                </small>
              )} */}
            </div>

            <div className="" style={{ marginTop: "30px" }}>
              <button
                className="btn btn-light me-2"
                onClick={() => {
                  setDateFilter("");
                  setStatusFilter("");
                  setSearchTerm("");
                }}
              >
                <i className="fas fa-times"></i>
              </button>
            </div>
          </div>
          {/* <div className="col-md-3">
            <label className="form-label">Filter by Status</label>
            <select
              className="form-select"
              value={statusFilter}
              onChange={e => setStatusFilter(e.target.value)}
            >
              <option value="">All Status</option>
              <option value="Completed">Completed</option>
              <option value="In Progress">In Progress</option>
              <option value="Pending">Pending</option>
              <option value="Blocked">Blocked</option>
            </select>
          </div> */}
          {/* <div className="col-md-3">
            <label className="form-label">Search</label>
            <input
              type="text"
              className="form-control"
              placeholder="Search work summary or title..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
            />
          </div> */}
          <div className="mt-4" >

            <button
              className="btn btn-primary"
              onClick={showAddModal}
            >
              <i className="fas fa-plus"></i>
            </button>
          </div>
        </div>

        <TableContainer
          columns={columns}
          data={filteredWorkLogs}
          isGlobalFilter={false}
          isPagination={true}
          SearchPlaceholder="Search work logs..."
          pagination="pagination"
          paginationWrapper="dataTables_paginate paging_simple_numbers"
          tableClass="table-bordered table-nowrap dt-responsive nowrap w-100 dataTable no-footer dtr-inline"
        />

        {filteredWorkLogs.length == 0 && (
          <div className="text-center py-5">
            <i className="fas fa-clipboard-list fa-3x text-muted mb-3"></i>
            <h4 className="text-muted">No work logs found</h4>
            <p className="text-muted">
              {dateFilter ? `No work logs for selected date` : `No work logs for today`}
            </p>
            {/* <button
              className="btn btn-primary"
              onClick={showAddModal}
            >
              <i className="fas fa-plus me-1"></i> Add Your First Work Log
            </button> */}
          </div>
        )}

        {/* Work Log Modal */}
        <WorkLogModal
          isOpen={workLogModal}
          toggle={handleModalClose}
          onSubmit={handleWorkLogSubmit}
          workLog={editingWorkLog}
          isLoading={creatingWorkLog || updatingWorkLog}
          projects={selectableProjects}
          user={user}
        />

        {/* Delete Confirmation Modal */}
        <DeleteModal
          show={deleteModal}
          onDeleteClick={handleDeleteWorkLog}
          loader={deletingWorkLog}
          onCloseClick={() => setDeleteModal(false)}
        />
      </div>
    </div>
  );
};

export default AllWorkLogs;

// import Breadcrumb from "components/Common/Breadcrumb";
// import TableContainer from "components/Common/DataTableContainer";
// import DashboardLoader from "components/Loader/DashboardLoader";
// import DeleteModal from "pages/Calendar/DeleteModal";
// import React, { useMemo, useState } from "react";
// import { useSelector } from "react-redux";
// import { Link } from "react-router-dom";
// import { selectUser } from "store/features/auth/authSlice";
// import {
//   useGetAllWorkLogsQuery,
//   useDeleteWorkLogMutation,
// } from "store/features/client/clientManagementApi";
// import { useGetAllProjectsQuery } from "store/features/client/clientManagementApi";

// const AllWorkLogs = () => {
//   const user = useSelector(selectUser);

//   const { data: workLogs, isLoading, refetch } = useGetAllWorkLogsQuery();
//   const { data: projects = [] } = useGetAllProjectsQuery();
//   const [deleteWorkLog, { isLoading: deletingWorkLog }] = useDeleteWorkLogMutation();

//   console.log("workLogsworkLogs", workLogs);
//   const [deleteModal, setDeleteModal] = useState(false);
//   const [selectedWorkLogId, setSelectedWorkLogId] = useState(null);
//   const [dateFilter, setDateFilter] = useState("");
//   const [statusFilter, setStatusFilter] = useState("");
//   const [searchTerm, setSearchTerm] = useState("");

//   // Get today's date in YYYY-MM-DD format
//   const getTodayDate = () => {
//     return new Date().toISOString().split('T')[0];
//   };

//   // Get project name by ID
//   const getProjectName = projectId => {
//     if (!projectId) return "No Project";
//     const project = projects.find(project => project.id === projectId);
//     return project ? project.name : "Unknown Project";
//   };

//   // Get status badge
//   const getStatusBadge = status => {
//     const statusConfig = {
//       'Completed': 'success',
//       'In Progress': 'primary',
//       'Pending': 'warning',
//       'Blocked': 'danger'
//     };
//     const color = statusConfig[status] || 'secondary';
//     return `<span class="badge bg-${color}">${status}</span>`;
//   };

//   // Filter work logs based on selected filters and user role
//   const filteredWorkLogs = useMemo(() => {
//     if (!workLogs) return [];

//     let filtered = workLogs;

//     // Apply date filter (default to today if no filter)
//     if (!dateFilter) {
//       filtered = filtered.filter(log => log.date === getTodayDate());
//     } else {
//       filtered = filtered.filter(log => log.date === dateFilter);
//     }

//     // Apply status filter
//     if (statusFilter) {
//       filtered = filtered.filter(log => log.work_status === statusFilter);
//     }

//     // Apply search filter
//     if (searchTerm) {
//       const term = searchTerm.toLowerCase();
//       filtered = filtered.filter(log =>
//         log.work_summary?.toLowerCase().includes(term) ||
//         log.title?.toLowerCase().includes(term) ||
//         log.employer_name?.toLowerCase().includes(term)
//       );
//     }

//     // Apply role-based filtering
//     if (user.role !== "super_admin" && user.role !== "admin") {
//       filtered = filtered.filter(log => log.employer_id === user.id);
//     }

//     return filtered;
//   }, [workLogs, dateFilter, statusFilter, searchTerm, user]);

//   const showDeleteModal = id => {
//     setSelectedWorkLogId(id);
//     setDeleteModal(true);
//   };

//   const handleDeleteWorkLog = () => {
//     deleteWorkLog(selectedWorkLogId);
//     setDeleteModal(false);
//   };

//   const columns = useMemo(() => [
//     {
//       header: "Employee",
//       accessorKey: "employer_name",
//       enableColumnFilter: false,
//       enableSorting: true,
//       cell: ({ row }) => (
//         <div>
//           <div className="fw-semibold">{row.original.employer_name}</div>
//           <small className="text-muted">{row.original.employer_role}</small>
//         </div>
//       ),
//     },
//     {
//       header: "Project",
//       accessorKey: "project_id",
//       enableColumnFilter: false,
//       enableSorting: true,
//       cell: ({ row }) => (
//         <div className="fw-semibold">
//           {getProjectName(row.original.project_id)}
//         </div>
//       ),
//     },
//     {
//       header: "Title",
//       accessorKey: "title",
//       enableColumnFilter: false,
//       enableSorting: true,
//       cell: ({ row }) => (
//         <div className="fw-semibold">
//           {row.original.title || "No Title"}
//         </div>
//       ),
//     },
//     {
//       header: "Work Summary",
//       accessorKey: "work_summary",
//       enableColumnFilter: false,
//       enableSorting: true,
//       cell: ({ row }) => (
//         <div>
//           <div className="fw-semibold">{row.original.work_summary}</div>
//           {row.original.work_summary && (
//             <small className="text-muted">
//               {row.original.work_summary.length > 100
//                 ? `${row.original.work_summary.substring(0, 100)}...`
//                 : row.original.work_summary
//               }
//             </small>
//           )}
//         </div>
//       ),
//     },
//     {
//       header: "Date",
//       accessorKey: "date",
//       enableColumnFilter: false,
//       enableSorting: true,
//       cell: ({ row }) => (
//         <div className="fw-semibold">
//           {new Date(row.original.date).toLocaleDateString()}
//         </div>
//       ),
//     },
//     {
//       header: "Hours",
//       accessorKey: "hours_spent",
//       enableColumnFilter: false,
//       enableSorting: true,
//       cell: ({ row }) => (
//         <div className="text-center">
//           <span className="badge bg-info fs-6">{row.original.hours_spent}h</span>
//         </div>
//       ),
//     },
//     {
//       header: "Status",
//       accessorKey: "work_status",
//       enableColumnFilter: false,
//       enableSorting: true,
//       cell: ({ row }) => (
//         <div
//           dangerouslySetInnerHTML={{
//             __html: getStatusBadge(row.original.work_status),
//           }}
//         />
//       ),
//     },
//     {
//       header: "Action",
//       accessorKey: "action",
//       enableColumnFilter: false,
//       enableSorting: true,
//       cell: ({ row }) => (
//         <div className="d-flex align-items-center gap-1">
//           <Link
//             to={`/worklog/${row.original.id}`}
//             className="btn btn-info btn-sm"
//           >
//             <i className="fas fa-eye me-1"></i> View
//           </Link>

//           {(user?.role === "super_admin" || user?.role === "admin") && (
//             <>
//               <Link
//                 to={`/worklog/edit/${row.original.id}`}
//                 className="btn btn-warning btn-sm"
//               >
//                 <i className="fas fa-edit me-1"></i> Edit
//               </Link>
//               <button
//                 className="btn btn-danger btn-sm"
//                 onClick={() => showDeleteModal(row.original.id)}
//               >
//                 <i className="fas fa-trash me-1"></i>
//                 {deletingWorkLog ? "Deleting..." : "Delete"}
//               </button>
//             </>
//           )}
//         </div>
//       ),
//     },
//   ], [projects, deletingWorkLog, user]);

//   if (isLoading) {
//     return <DashboardLoader />;
//   }

//   return (
//     <div className="page-content">
//       <div className="container-fluid">
//         <Breadcrumb title="Work Log" breadcrumbItem="Daily Work Logs" />

//         {/* Quick Stats */}
//         <div className="row mb-4">
//           <div className="col-md-3">
//             <div className="card bg-primary text-white">
//               <div className="card-body">
//                 <div className="d-flex align-items-center">
//                   <div className="flex-grow-1">
//                     <h4 className="mb-0">{filteredWorkLogs.length}</h4>
//                     <span>Total Entries</span>
//                   </div>
//                   <div className="flex-shrink-0">
//                     <i className="fas fa-clipboard-list fa-2x"></i>
//                   </div>
//                 </div>
//               </div>
//             </div>
//           </div>
//           <div className="col-md-3">
//             <div className="card bg-success text-white">
//               <div className="card-body">
//                 <div className="d-flex align-items-center">
//                   <div className="flex-grow-1">
//                     <h4 className="mb-0">
//                       {filteredWorkLogs.reduce((sum, log) => sum + parseFloat(log.hours_spent || 0), 0).toFixed(1)}
//                     </h4>
//                     <span>Total Hours</span>
//                   </div>
//                   <div className="flex-shrink-0">
//                     <i className="fas fa-clock fa-2x"></i>
//                   </div>
//                 </div>
//               </div>
//             </div>
//           </div>
//           <div className="col-md-3">
//             <div className="card bg-info text-white">
//               <div className="card-body">
//                 <div className="d-flex align-items-center">
//                   <div className="flex-grow-1">
//                     <h4 className="mb-0">
//                       {filteredWorkLogs.filter(log => log.work_status === 'Completed').length}
//                     </h4>
//                     <span>Completed</span>
//                   </div>
//                   <div className="flex-shrink-0">
//                     <i className="fas fa-check-circle fa-2x"></i>
//                   </div>
//                 </div>
//               </div>
//             </div>
//           </div>
//           <div className="col-md-3">
//             <div className="card bg-warning text-white">
//               <div className="card-body">
//                 <div className="d-flex align-items-center">
//                   <div className="flex-grow-1">
//                     <h4 className="mb-0">
//                       {filteredWorkLogs.filter(log => log.work_status === 'In Progress').length}
//                     </h4>
//                     <span>In Progress</span>
//                   </div>
//                   <div className="flex-shrink-0">
//                     <i className="fas fa-spinner fa-2x"></i>
//                   </div>
//                 </div>
//               </div>
//             </div>
//           </div>
//         </div>

//         {/* Filters Section */}
//         <div className="row mb-3">
//           <div className="col-md-3">
//             <label className="form-label">Date Filter</label>
//             <input
//               type="date"
//               className="form-control"
//               value={dateFilter}
//               onChange={e => setDateFilter(e.target.value)}
//             />
//             {dateFilter && (
//               <small
//                 className="text-primary cursor-pointer"
//                 onClick={() => setDateFilter("")}
//               >
//                 Show Today's Logs
//               </small>
//             )}
//           </div>
//           <div className="col-md-3">
//             <label className="form-label">Filter by Status</label>
//             <select
//               className="form-select"
//               value={statusFilter}
//               onChange={e => setStatusFilter(e.target.value)}
//             >
//               <option value="">All Status</option>
//               <option value="Completed">Completed</option>
//               <option value="In Progress">In Progress</option>
//               <option value="Pending">Pending</option>
//               <option value="Blocked">Blocked</option>
//             </select>
//           </div>
//           <div className="col-md-3">
//             <label className="form-label">Search</label>
//             <input
//               type="text"
//               className="form-control"
//               placeholder="Search work summary or title..."
//               value={searchTerm}
//               onChange={e => setSearchTerm(e.target.value)}
//             />
//           </div>
//           <div className="col-md-3 d-flex align-items-end">
//             <button
//               className="btn btn-light me-2"
//               onClick={() => {
//                 setDateFilter("");
//                 setStatusFilter("");
//                 setSearchTerm("");
//               }}
//             >
//               <i className="fas fa-times me-1"></i> Clear
//             </button>
//             <Link to="/add-worklog" className="btn btn-primary">
//               <i className="fas fa-plus me-1"></i> Add Work Log
//             </Link>
//           </div>
//         </div>

//         <TableContainer
//           columns={columns}
//           data={filteredWorkLogs}
//           isGlobalFilter={false}
//           isPagination={true}
//           SearchPlaceholder="Search work logs..."
//           pagination="pagination"
//           paginationWrapper="dataTables_paginate paging_simple_numbers"
//           tableClass="table-bordered table-nowrap dt-responsive nowrap w-100 dataTable no-footer dtr-inline"
//         />

//         {filteredWorkLogs.length === 0 && (
//           <div className="text-center py-5">
//             <i className="fas fa-clipboard-list fa-3x text-muted mb-3"></i>
//             <h4 className="text-muted">No work logs found</h4>
//             <p className="text-muted">
//               {dateFilter ? `No work logs for selected date` : `No work logs for today`}
//             </p>
//             <Link to="/worklog/add" className="btn btn-primary">
//               <i className="fas fa-plus me-1"></i> Add Your First Work Log
//             </Link>
//           </div>
//         )}

//         <DeleteModal
//           show={deleteModal}
//           onDeleteClick={handleDeleteWorkLog}
//           loader={deletingWorkLog}
//           onCloseClick={() => setDeleteModal(false)}
//         />
//       </div>
//     </div>
//   );
// };

// export default AllWorkLogs;